import { Routes, Route, Navigate } from 'react-router-dom';
import { App } from './App';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { MapPage } from './pages/MapPage';
import { PublicMapPage } from './pages/PublicMapPage';
import { EmbedPage } from './pages/EmbedPage';
import { SettingsPage } from './pages/SettingsPage';
import { AdminPage } from './pages/AdminPage';
import { LandingPage } from './pages/LandingPage';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { PinGate } from './components/PinGate';
import { getStoredToken } from './services/apiClient';

/**
 * App mode: 'personal' for direct map access (olyakolya.love), 'saas' for full platform
 * Set via VITE_APP_MODE environment variable
 */
const isPersonalMode = import.meta.env.VITE_APP_MODE === 'personal';

/**
 * Home route that shows LandingPage for visitors or redirects to Dashboard for logged-in users
 */
function HomeRoute() {
  const token = getStoredToken();

  if (token) {
    return <Navigate to="/dashboard" replace />;
  }

  return <LandingPage />;
}

/**
 * Personal mode router - minimal routes, direct map access
 * Used for standalone deployments like olyakolya.love
 */
function PersonalRouter() {
  return (
    <Routes>
      {/* PIN gate protects root — user must enter secret date */}
      <Route path="/" element={<PinGate><App /></PinGate>} />
      {/* Keep public/embed routes for sharing functionality */}
      <Route path="/p/:slug" element={<PublicMapPage />} />
      <Route path="/embed/:slug" element={<EmbedPage />} />
      {/* Redirect any other route to root */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * SaaS mode router - full platform with auth, dashboard, settings
 */
function SaaSRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* SPRINT-5.2: Public map view (no auth required) */}
      <Route path="/p/:slug" element={<PublicMapPage />} />
      {/* SPRINT-8.5: Embeddable map view (no auth required) */}
      <Route path="/embed/:slug" element={<EmbedPage />} />
      {/* SPRINT-8.4: Accept collaborator invitation */}
      <Route path="/invite/:token" element={<AcceptInvitePage />} />
      {/* SPRINT-7.1: Landing page for visitors, redirect to dashboard for logged-in users */}
      <Route path="/" element={<HomeRoute />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/map/:id"
        element={
          <ProtectedRoute>
            <MapPage />
          </ProtectedRoute>
        }
      />
      {/* SPRINT-6.7: User settings with billing management */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      {/* SPRINT-7.3: Admin dashboard with user list */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export function AppRouter() {
  // Personal mode: direct map access, no auth
  // SaaS mode: full platform with auth flow
  return isPersonalMode ? <PersonalRouter /> : <SaaSRouter />;
}
